// src/pages/markets/board/board/CategoryMenu.tsx
//
// Twenty entries is past what a reader scans, so the field at the top is what
// makes the menu usable rather than a decoration.

import { useMemo, useState } from 'react';
import { Box, IconButton, InputBase, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { buildCategories, categoryKey, CONVICTION_SECTION, type CategoryConfig } from './categories';
import { useBoardGroups } from '../state/boardGroupsContext';
import type { BoardSchema, ColumnId } from '../api/boardTypes';
import { SectionLabel as BaseSectionLabel } from '../../../../components/common/page/SectionLabel';

export interface CategoryMenuProps {
    anchorEl: HTMLElement | null;
    schema: BoardSchema;
    current: { source: ColumnId; group: string | null };
    onPick: (category: CategoryConfig) => void;
    onClose: () => void;
    // Finger-sized rows and a 16 px filter field, for the phone sheet.
    touch?: boolean;
}

// Keys the Menu owns: focus returns to the row before they bubble.
const ROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Home', 'End', 'Escape']);

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <BaseSectionLabel
            sx={{
                letterSpacing: '0.08em',
                px: 1.25,
                pt: 1,
                pb: 0.4,
                mb: 0,
            }}
        >
            {children}
        </BaseSectionLabel>
    );
}

export function CategoryMenu({ anchorEl, schema, current, onPick, onClose, touch = false }: CategoryMenuProps) {
    const theme = useTheme();
    const [query, setQuery] = useState('');
    const groups = useBoardGroups();
    const { isAdmin, isSuccess, offered, resolve, setVisible } = groups;

    // Titles come from the group documents, so a name an admin typed is both
    // what gets searched and what gets displayed.
    const all = useMemo(() => buildCategories(schema, groups), [schema, groups]);
    const currentKey = categoryKey(current.source, current.group);

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return all;
        return all.filter(c => c.title.toLowerCase().includes(q));
    }, [all, query]);

    const conviction = shown.filter(
        c => c.section === CONVICTION_SECTION && (c.group === null || offered(c.group)),
    );
    const discovery = shown.filter(c => c.section !== CONVICTION_SECTION);

    // The field is not unmounted with the Menu, so every exit clears it itself:
    // reopening on a pane must not hide 19 categories behind stale text.
    const handleClose = () => {
        setQuery('');
        onClose();
    };

    // MUI's Menu takes Tab for itself and MenuList's arrows only move between
    // rows, so a nested control needs its own route in and out. ArrowRight
    // enters the row's toggle; ArrowLeft comes back.
    const handleRowKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
        if (e.key !== 'ArrowRight') return;
        const button = e.currentTarget.querySelector('button');
        if (!button) return;
        e.preventDefault();
        e.stopPropagation();
        button.focus();
    };

    const handleToggleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        const row = e.currentTarget.closest('li');
        if (!row) return;
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            row.focus();
            return;
        }
        // Row navigation and dismissal stay the Menu's: focus goes back to the
        // row first, so MenuList moves from a node it owns.
        if (ROW_KEYS.has(e.key)) row.focus();
    };

    const handlePick = (c: CategoryConfig) => {
        setQuery('');
        onPick(c);
    };

    const renderItem = (c: CategoryConfig) => {
        const key = categoryKey(c.source, c.group);
        const selected = key === currentKey;
        const group = c.group;
        // Only a Conviction row has a group, so only it can be hidden or toggled.
        const isVisible = group !== null && resolve(group).visible;
        const hiddenForAdmin = isAdmin && group !== null && !isVisible;
        // Offered only once the read succeeded: while it has not, `visible` is
        // the default set standing in, and a toggle would write that guess.
        const canCurate = isAdmin && isSuccess && group !== null;
        return (
            <MenuItem
                key={key}
                // A radio item, so the current category is announced rather than
                // carried only by the check mark.
                role="menuitemradio"
                selected={selected}
                aria-checked={selected}
                onClick={() => handlePick(c)}
                onKeyDown={handleRowKeyDown}
                data-hidden={hiddenForAdmin || undefined}
                sx={{
                    minHeight: touch ? 44 : 28,
                    py: 0.25,
                    px: 1.25,
                    fontSize: touch ? '15px' : '12px',
                    opacity: hiddenForAdmin ? 0.45 : 1,
                    '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.14),
                        color: 'primary.main',
                        fontWeight: 700,
                    },
                }}
            >
                <ListItemText
                    primaryTypographyProps={{ fontSize: touch ? '15px' : '12px', fontWeight: 'inherit' }}
                >
                    {c.title}
                </ListItemText>
                {selected && <CheckIcon sx={{ fontSize: 14, ml: 1 }} />}
                {canCurate && (
                    <Tooltip title={`${isVisible ? 'Hide' : 'Show'} ${c.title} for other users`}>
                        <IconButton
                            size="small"
                            aria-label={`${isVisible ? 'Hide' : 'Show'} ${c.title} for other users`}
                            onClick={e => {
                                // Neither picks the category nor closes the menu.
                                e.stopPropagation();
                                e.preventDefault();
                                // A rejected write (demoted admin, offline) is
                                // logged rather than left unhandled.
                                void setVisible(group, !isVisible).catch(err => {
                                    console.warn('Could not change category visibility', err);
                                });
                            }}
                            onKeyDown={handleToggleKeyDown}
                            sx={touch ? { width: 44, height: 44, ml: 0.5 } : { p: 0.25, ml: 0.5 }}
                        >
                            {isVisible ? (
                                <VisibilityOutlinedIcon sx={{ fontSize: touch ? 20 : 13 }} />
                            ) : (
                                <VisibilityOffOutlinedIcon sx={{ fontSize: touch ? 20 : 13 }} />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            </MenuItem>
        );
    };

    return (
        <Menu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
                paper: {
                    sx: touch
                        ? { width: 'min(320px, calc(100vw - 32px))', maxHeight: 'min(420px, 70vh)' }
                        : { width: 264, maxHeight: 420 },
                },
            }}
            MenuListProps={{ dense: true, sx: { py: 0 } }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
                // Printable keys belong to the field rather than to the Menu's
                // type-ahead, and so do Home and End: MenuList preventDefaults
                // both, which would jump focus out instead of moving the caret.
                // Escape, Tab and the arrows stay the Menu's, or the field would
                // swallow dismissal and navigation.
                onKeyDown={e => {
                    if (e.key.length === 1 || e.key === 'Home' || e.key === 'End') {
                        e.stopPropagation();
                    }
                }}
            >
                <SearchIcon sx={{ fontSize: touch ? 20 : 14, color: 'text.secondary' }} />
                <InputBase
                    // The keyboard would open over the list on a phone.
                    autoFocus={!touch}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Filter categories"
                    sx={{ fontSize: touch ? '16px' : '12px', flex: 1, minHeight: touch ? 44 : undefined }}
                />
            </Box>

            {/* Discovery leads: two entries a reader can take in at a glance, ahead
                of the eighteen-deep conviction list they would otherwise scroll past. */}
            {discovery.length > 0 && <SectionLabel>Discovery</SectionLabel>}
            {discovery.map(renderItem)}
            {conviction.length > 0 && <SectionLabel>Conviction</SectionLabel>}
            {conviction.map(renderItem)}
        </Menu>
    );
}
